/**
 * useMapCoordinates - Coordinate conversion utilities for OpenSeadragon
 * Elvis Aloha Collection - Arena Map Editor
 * 
 * Handles conversion between screen coordinates and native image coordinates.
 * All geometry is stored in native image pixel coordinates for consistency.
 */

import { useCallback, useRef, useEffect, useState } from 'react';
import OpenSeadragon from 'openseadragon';

/**
 * useMapCoordinates - Coordinate conversion utilities for OpenSeadragon
 *
 * @param {React.RefObject} viewerRef - ref holding the live OpenSeadragon viewer instance
 * @param {number} syncToken - REACTIVE value that changes only when the caller has
 *   confirmed the viewer instance exists AND its tiled image is open (e.g. bumped
 *   inside the viewer's 'open' handler, after any initial fitBounds()). Mutating
 *   viewerRef.current alone does NOT trigger a re-render, so callers MUST bump this
 *   token whenever the viewer/tiled-image readiness changes (initial open, chart
 *   switch, etc.) for this hook's effect to correctly (re)attach.
 */
export const useMapCoordinates = (viewerRef, syncToken = 0) => {
  // No default identity transform - null means "not yet computed", so consumers
  // can hide position-dependent geometry instead of flashing it at (0,0) scale 1.
  const [transform, setTransform] = useState(null);
  const rafIdRef = useRef(null);
  
  /**
   * Convert screen coordinates to native image coordinates
   */
  const screenToImageCoords = useCallback((screenX, screenY) => {
    const viewer = viewerRef.current;
    if (!viewer) return { x: 0, y: 0 };
    
    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage) return { x: 0, y: 0 };
    
    const containerRect = viewer.container.getBoundingClientRect();
    const viewportPoint = viewer.viewport.pointFromPixel(
      new OpenSeadragon.Point(screenX - containerRect.left, screenY - containerRect.top)
    );
    const imagePoint = tiledImage.viewportToImageCoordinates(viewportPoint);
    
    return { x: imagePoint.x, y: imagePoint.y };
  }, [viewerRef]);
  
  /**
   * Convert native image coordinates to screen coordinates
   */
  const imageToScreenCoords = useCallback((imageX, imageY) => {
    const viewer = viewerRef.current;
    if (!viewer) return { x: 0, y: 0 };
    
    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage) return { x: 0, y: 0 };
    
    const viewportPoint = tiledImage.imageToViewportCoordinates(
      new OpenSeadragon.Point(imageX, imageY)
    );
    const screenPoint = viewer.viewport.viewportToViewerElementCoordinates(viewportPoint);
    
    return { x: screenPoint.x, y: screenPoint.y };
  }, [viewerRef]);
  
  /**
   * Get current SVG transform for overlays
   */
  const updateTransform = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    
    try {
      const tiledImage = viewer.world.getItemAt(0);
      if (!tiledImage) return;
      
      const bounds = tiledImage.getBounds();
      const topLeft = viewer.viewport.viewportToViewerElementCoordinates(
        new OpenSeadragon.Point(bounds.x, bounds.y)
      );
      const bottomRight = viewer.viewport.viewportToViewerElementCoordinates(
        new OpenSeadragon.Point(bounds.x + bounds.width, bounds.y + bounds.height)
      );
      
      const imageWidth = tiledImage.source.width;
      const screenWidth = bottomRight.x - topLeft.x;
      const scale = screenWidth / imageWidth;
      
      if (!Number.isFinite(scale) || scale <= 0) return;
      
      setTransform({ scale, translateX: topLeft.x, translateY: topLeft.y });
    } catch (e) {
      // Viewer mid-teardown or tiledImage not fully initialized - skip this tick,
      // the next event (or the immediate post-attach call) will retry.
    }
  }, [viewerRef]);

  // Coalesce rapid-fire viewer events (continuous pan/zoom) into a single
  // transform recompute per animation frame, instead of one React state
  // update per raw OpenSeadragon event.
  const scheduleUpdate = useCallback(() => {
    if (rafIdRef.current != null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      updateTransform();
    });
  }, [updateTransform]);

  // Attach/detach viewer event handlers. This effect is keyed on `syncToken`
  // (a reactive value bumped by the caller once the viewer + tiled image are
  // confirmed ready) rather than on the viewerRef object itself, since
  // mutating a ref's `.current` never triggers a React effect re-run.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    
    const events = ['open', 'animation', 'animation-finish', 'pan', 'zoom', 'resize', 'rotate'];
    events.forEach(evt => viewer.addHandler(evt, scheduleUpdate));
    
    // Compute the initial transform immediately (no need to wait for a frame)
    // now that the viewer/tiled-image are guaranteed ready.
    updateTransform();
    
    return () => {
      events.forEach(evt => viewer.removeHandler(evt, scheduleUpdate));
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [syncToken, viewerRef, scheduleUpdate, updateTransform]);
  
  /**
   * Check if a point is inside a rectangle (in image coordinates)
   */
  const pointInRect = useCallback((px, py, rect) => {
    return px >= rect.x && px <= rect.x + rect.width &&
           py >= rect.y && py <= rect.y + rect.height;
  }, []);
  
  /**
   * Check if a point is inside a polygon (in image coordinates)
   */
  const pointInPolygon = useCallback((px, py, points) => {
    if (!points || points.length < 3) return false;
    
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].x, yi = points[i].y;
      const xj = points[j].x, yj = points[j].y;
      
      if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }, []);
  
  /**
   * Check if a point is near a marker (within radius in image coordinates)
   */
  const pointNearMarker = useCallback((px, py, marker, radius = 15) => {
    const dx = px - marker.x;
    const dy = py - marker.y;
    return Math.sqrt(dx * dx + dy * dy) <= radius;
  }, []);
  
  /**
   * Find overlay at given image coordinates
   */
  const findOverlayAtPoint = useCallback((px, py, overlays) => {
    // Check in reverse order (topmost first)
    for (let i = overlays.length - 1; i >= 0; i--) {
      const overlay = overlays[i];
      if (overlay.visible === false) continue;
      
      if (overlay.type === 'rect') {
        if (pointInRect(px, py, overlay)) return overlay;
      } else if (overlay.type === 'polygon') {
        if (pointInPolygon(px, py, overlay.points)) return overlay;
      } else if (overlay.type === 'marker') {
        if (pointNearMarker(px, py, overlay)) return overlay;
      }
    }
    return null;
  }, [pointInRect, pointInPolygon, pointNearMarker]);
  
  return {
    transform,
    screenToImageCoords,
    imageToScreenCoords,
    updateTransform,
    pointInRect,
    pointInPolygon,
    pointNearMarker,
    findOverlayAtPoint
  };
};

export default useMapCoordinates;
